/**
 * This component is for displaying a map where the marker data will be coming from multiple attributes.
 * This map component expects data in GeoJSON format. In case an attribute provides a different format data, It should be transformed
 * to GeoJSON format and provided to this component. This map can plot lines, polygons, draw pins as we give it all types of data in
 * GeoJSON format. There should be a field in properties of GeoJSON which should provide further information on what marker icon to be
 * rendered. And what is the color, shape and other details of the line & marker etc.
 */

import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import {
    GoogleMap,
    InfoBox,
    Libraries,
    useJsApiLoader,
} from '@react-google-maps/api'
import { Box, LinearProgress } from '@mui/material'
import MapControls from '../../../Common/MapControls'
import { useAppSelector } from '../../../../redux/hook'
import { getDataSourceKey } from '../../../../utils/MiscellaneousUtils'
import {
    defaultGeoFenceRadius,
    googleMapOptions,
    locationMarkerSVG,
    ShipmentAttributes,
    ShipmentStatus,
} from '../../../../constants/constants'
import { formatDateTimeWithUserPreferences } from '../../../../utils/DateAndTimeUtils'
import {
    capitalizeFirstLetterInEachWord,
    capitalizeFirstLetterOfWord,
} from '../../../../utils/StringUtils'
import {
    getRoadDistanceAndTime,
    isCircleMap,
    isGeometryWithCoordinates,
    LatLng,
} from '../../../../utils/MapUtils'
import DashedLine from '../../../Common/ReactIcons/DashedLine'
import LocationMarker from '../../../Common/ReactIcons/LocationMarker'
import VesselIcon from '../../../Common/ReactIcons/VesselIcon'
import SeaPortIcon from '../../../Common/ReactIcons/SeaPortIcon'
import { useTranslation } from 'react-i18next'

const containerStyle = {
    width: '100%',
    height: '100%',
}

const legendContainerStyle = {
    padding: '10px',
    borderRadius: '5px',
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    minHeight: '50px',
    maxHeight: '100px',
}

const options = {
    zoom: 8,
}

const maxZoom = 21
const minZoom = 1

export interface GeoJSONDataSource {
    attributeId: string
    attributeName: string //Represents displayName
    entity?: string
    entityInstanceId?: string
    geojson: GeoJSON.FeatureCollection
    color: string
    isSelected?: boolean /*This isSelected property represents whether the legend for this attributeName beneath the map is toggled or not. It is not related to whether the 
    attribute is checked or not in the dropdowns above the Map Widget.*/
}

interface IInfoWindow {
    open: boolean
    content: string
    position: { lat: number; lng: number }
}

const libraries: Libraries = ['places']

const toFiniteNumber = (value: unknown): number | null => {
    if (value === null || value === undefined) {
        return null
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null
    }

    if (typeof value === 'string') {
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : null
    }

    return null
}

const toLatLng = (geometry: GeoJSON.Geometry | null): LatLng | null => {
    if (!isGeometryWithCoordinates(geometry)) {
        return null
    }

    const [lngRaw, latRaw] = geometry.coordinates
    const lat = toFiniteNumber(latRaw)
    const lng = toFiniteNumber(lngRaw)

    if (lat === null || lng === null) {
        return null
    }

    return { lat, lng }
}

const MultiDataSourceMapComponent = ({
    alarmCritical,
    dataSources: initialDataSources,
    dynamicDataLayersKeys,
    preConfiguredIcons,
    shipmentStatus,
}: {
    alarmCritical: boolean
    dataSources: GeoJSONDataSource[]
    dynamicDataLayersKeys: string[]
    preConfiguredIcons: boolean
    shipmentStatus: string
}): ReactElement => {
    const { t } = useTranslation()
    const [dataSources, setDataSources] = useState<GeoJSONDataSource[]>([])
    const dataSourcesRef = useRef<GeoJSONDataSource[]>([])
    const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries,
    })

    const [map, setMap] = useState<google.maps.Map | null>(null)
    const layerRefs = useRef<Record<string, google.maps.Data | null>>({}) // Hold reference for each data layer
    const circleRefs = useRef<Record<string, google.maps.Circle[]>>({}) // Hold reference for circles
    const [infoWindow, setInfoWindow] = useState<IInfoWindow>({} as IInfoWindow)
    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )

    const detailMapContainerRef = useRef<HTMLDivElement>(null)
    const [isDetailFullscreen, setIsDetailFullscreen] = useState(false)
    const [mapZoom, setMapZoom] = useState(options.zoom)

    const handleDetailToggleFullscreen = useCallback(() => {
        if (document.fullscreenElement) {
            document.exitFullscreen()
        } else if (detailMapContainerRef.current) {
            detailMapContainerRef.current.requestFullscreen()
        }
    }, [])

    useEffect(() => {
        const onFsChange = () => {
            setIsDetailFullscreen(!!document.fullscreenElement)
            if (map) {
                setTimeout(() => {
                    google.maps.event.trigger(map, 'resize')
                    if (dataSources.length > 0) {
                        const bounds = calculateBounds(dataSources)
                        if (bounds && !bounds.isEmpty()) {
                            map.fitBounds(bounds)
                        }
                    }
                }, 100)
            }
        }
        document.addEventListener('fullscreenchange', onFsChange)
        return () =>
            document.removeEventListener('fullscreenchange', onFsChange)
    }, [map, dataSources])

    const handleZoomIn = useCallback(() => {
        if (map) {
            const current = map.getZoom() || 1
            const newZoom = Math.min(current + 1, maxZoom)
            map.setZoom(newZoom)
            setMapZoom(newZoom)
        }
    }, [map])

    const handleZoomOut = useCallback(() => {
        if (map) {
            const current = map.getZoom() || 1
            const newZoom = Math.max(current - 1, minZoom)
            map.setZoom(newZoom)
            setMapZoom(newZoom)
        }
    }, [map])

    const handleZoomChanged = useCallback(() => {
        if (map) {
            setMapZoom(map.getZoom() ?? options.zoom)
        }
    }, [map])

    // Resize map when layout changes
    useEffect(() => {
        if (map) {
            const timer = setTimeout(() => {
                google.maps.event.trigger(map, 'resize')
                if (dataSources.length > 0) {
                    const bounds = calculateBounds(dataSources)
                    if (bounds && !bounds.isEmpty()) {
                        map.fitBounds(bounds)
                    }
                }
            }, 350)
            return () => clearTimeout(timer)
        }
        return undefined
    }, [map])

    useEffect(() => {
        setDataSources(initialDataSources)
    }, [initialDataSources])

    useEffect(() => {
        dataSourcesRef.current = dataSources
    }, [dataSources])

    // Function to calculate bounds from all data sources
    const calculateBounds = useCallback((dataSources: GeoJSONDataSource[]) => {
        const bounds = new google.maps.LatLngBounds()
        dataSources.forEach((source) => {
            source.geojson.features.forEach((feature) => {
                if (feature.geometry.type === 'Point') {
                    bounds.extend({
                        lat: feature.geometry.coordinates[1],
                        lng: feature.geometry.coordinates[0],
                    })
                } else if (feature.geometry.type === 'LineString') {
                    feature.geometry.coordinates.forEach((coord) => {
                        bounds.extend({
                            lat: coord[1],
                            lng: coord[0],
                        })
                    })
                } else if (feature.geometry.type === 'Polygon') {
                    feature.geometry.coordinates[0].forEach((coord) => {
                        bounds.extend({
                            lat: coord[1],
                            lng: coord[0],
                        })
                    })
                }
                // Add other geometry types (MultiPoint, MultiLineString, MultiPolygon) if needed
            })
        })
        return bounds
    }, [])

    const onLoad = useCallback((map: google.maps.Map) => {
        setMap(map)
    }, [])

    // Add or remove GeoJSON layers dynamically based on incoming dataSources
    useEffect(() => {
        if (!map) {
            return
        }
        dataSources.forEach((source) => {
            // If the layer exists, ensure it is displayed. Update its data if required.
            const layerKey = getDataSourceKey(source)
            const dataLayer = layerRefs.current[layerKey]
            if (dataLayer) {
                if (source.isSelected) {
                    dataLayer?.setMap(map)
                    if (dynamicDataLayersKeys.includes(layerKey)) {
                        /**
                         * This operation is required because for dynamic timerseries
                         * attributes their map data might have updated. So not only enabling their
                         * map, we also remove all features in that data layer and create them
                         * freshly with updated geojson.
                         */
                        dataLayer?.forEach((feature) =>
                            dataLayer.remove(feature),
                        ) // Clear existing features
                        dataLayer?.addGeoJson(source.geojson) // Add the updated GeoJSON
                    }
                } else {
                    dataLayer?.setMap(null)
                }
            } else {
                // Create a new data layer if it doesn't exist
                const dataLayer = new google.maps.Data({ map })
                dataLayer.addGeoJson(source.geojson)
                dataLayer.setStyle((feature: google.maps.Data.Feature) => {
                    const config = feature.getProperty('config') as any
                    const geometryType = feature.getGeometry()?.getType() || ''

                    if (config?.icon === 'dot') {
                        if (alarmCritical) {
                            return {
                                icon: {
                                    url: "data:image/svg+xml;charset=UTF-8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32'><circle cx='16' cy='16' r='8' fill='white' stroke='black'/></svg>",
                                    scaledSize: new window.google.maps.Size(
                                        16,
                                        16,
                                    ), // Adjust size for small dot
                                    anchor: new google.maps.Point(8, 8), // Center the dot
                                },
                            }
                        }
                        return {
                            icon: {
                                url: "data:image/svg+xml;charset=UTF-8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32'><circle cx='16' cy='16' r='8' fill='white' stroke='black'/></svg>",
                                scaledSize: new window.google.maps.Size(16, 16), // Adjust size for small dot
                                anchor: new google.maps.Point(8, 8), // Center the dot
                            },
                        }
                    } else if (geometryType === 'Point') {
                        // For non-dot markers, use the default Google marker icon with a colored background
                        return {
                            icon: {
                                url: preConfiguredIcons
                                    ? config?.url
                                        ? config?.url
                                        : '/assets/images/map/marker-blue.png'
                                    : undefined,
                                path: preConfiguredIcons
                                    ? config?.path
                                    : // ? config?.path
                                      // : locationMarkerSVG
                                      locationMarkerSVG, // Default tear drop shape
                                fillColor:
                                    preConfiguredIcons && config?.color
                                        ? config?.color
                                        : source.color,
                                fillOpacity: 1,
                                strokeWeight: 2,
                                strokeColor:
                                    preConfiguredIcons && config?.color
                                        ? config?.color
                                        : source.color,
                                scale: 1, // Adjust the scale as necessary
                                anchor: new google.maps.Point(12, 26),
                                scaledSize: new window.google.maps.Size(36, 36),
                            },
                        }
                    } else if (geometryType === 'LineString') {
                        return {
                            strokeColor:
                                preConfiguredIcons && config?.color
                                    ? config?.color
                                    : source.color, // Line color
                            strokeOpacity:
                                preConfiguredIcons &&
                                !isNaN(config.strokeOpacity)
                                    ? config.strokeOpacity
                                    : 1,
                            icons: [
                                {
                                    icon: {
                                        path: 'M 0,-1 0,1',
                                        strokeOpacity: 1,
                                        scale: 4,
                                    },
                                    offset: '0',
                                    repeat: preConfiguredIcons
                                        ? config.repeat
                                        : '0', // Adjust spacing between dashes
                                },
                            ],
                        }
                    }
                    return {
                        strokeColor:
                            preConfiguredIcons && config?.color
                                ? config?.color
                                : source.color,
                        fillColor:
                            preConfiguredIcons && config?.color
                                ? config?.color
                                : source.color,
                    }
                })

                dataLayer.addListener(
                    'mouseover',
                    (event: google.maps.Data.MouseEvent) => {
                        const latlng = event.latLng
                        const lat = latlng?.lat() || 0
                        const lng = latlng?.lng() || 0
                        const info: string[] = []
                        event.feature.forEachProperty((a: any, b) => {
                            if (b === 'config' || b === 'lat' || b === 'long') {
                                return
                            } else if (b === 'events') {
                                a?.forEach((event: { [key: string]: any }) => {
                                    let html = ''
                                    for (const key in event) {
                                        let value = event[key]
                                        if (key === 'date') {
                                            value =
                                                formatDateTimeWithUserPreferences(
                                                    value,
                                                    userLocalization,
                                                    'YYYY-MM-DD HH:mm:ss',
                                                )
                                        }
                                        html += `<strong>${capitalizeFirstLetterInEachWord(key)}:</strong> ${capitalizeFirstLetterOfWord(value)},<br/>`
                                    }
                                    info.push(
                                        html
                                            ? '<div style="margin-bottom: 10px;white-space: nowrap;">' +
                                                  html +
                                                  '</div>'
                                            : '',
                                    )
                                })
                            } else if (b === 'time') {
                                info.push(
                                    `<div style="margin-bottom: 10px;white-space: nowrap;"><strong>Lat, Long : </strong>${lat}, ${lng} @ ${formatDateTimeWithUserPreferences(
                                        a,
                                        userLocalization,
                                    )}</div>`,
                                )
                            } else if (b === 'readings') {
                                let html = ''
                                for (const key in a) {
                                    const formattedTime =
                                        formatDateTimeWithUserPreferences(
                                            key,
                                            userLocalization,
                                        )
                                    html += `${a[key]} @ ${formattedTime}<br/>`
                                }
                                info.push(
                                    html
                                        ? '<div style="margin-bottom: 10px;white-space: nowrap;">' +
                                              html +
                                              '</div>'
                                        : '',
                                )
                            } else {
                                info.push(
                                    `<div style="margin-bottom: 5px;white-space: nowrap;"><strong>${capitalizeFirstLetterInEachWord(b)}:</strong> ${capitalizeFirstLetterOfWord(a)}<br/></div>`,
                                )
                            }
                        })

                        if (info.length === 0) {
                            return
                        }
                        const content = `
                            <div>
                                ${info.join('')}
                            </div>`
                        setInfoWindow({
                            open: true,
                            content,
                            position: { lat, lng },
                        })
                    },
                )

                layerRefs.current[layerKey] = dataLayer
                if (!source.isSelected) {
                    dataLayer?.setMap(null)
                }
            }
            //Draw circles the data layer if it has radius property and Point type feature
            if (isCircleMap(source.geojson)) {
                const circles = circleRefs.current[layerKey] || []

                // Clear existing circles
                circles.forEach((circle) => circle.setMap(null))
                circleRefs.current[layerKey] = []

                if (source.isSelected) {
                    const newCircles: google.maps.Circle[] = []
                    source.geojson.features.forEach((feature) => {
                        if (
                            feature.geometry.type === 'Point' &&
                            feature.properties &&
                            (feature.properties as any).radius
                        ) {
                            const coordinates = feature.geometry.coordinates
                            const properties = feature.properties as any
                            const config = properties?.config
                            const lat = Number(coordinates[1])
                            const lng = Number(coordinates[0])
                            const radiusInMeters =
                                Number(properties?.radius) ||
                                defaultGeoFenceRadius

                            const circle = new google.maps.Circle({
                                strokeColor:
                                    preConfiguredIcons && config?.color
                                        ? config?.color
                                        : source.color,
                                strokeOpacity: 0.8,
                                strokeWeight: 2,
                                fillColor:
                                    preConfiguredIcons && config?.color
                                        ? config?.color
                                        : source.color,
                                fillOpacity: 0.35,
                                map,
                                center: { lat, lng },
                                radius: radiusInMeters,
                            })
                            newCircles.push(circle)
                        }
                    })
                    circleRefs.current[layerKey] = newCircles
                }
            }
        })

        // Hide layers that are not part of dataSources
        Object.keys(layerRefs.current).forEach((key) => {
            if (
                !dataSources.find((source) => getDataSourceKey(source) === key)
            ) {
                layerRefs.current[key]?.setMap(null)
                // Also hide circles
                circleRefs.current[key]?.forEach((circle) =>
                    circle.setMap(null),
                )
                delete circleRefs.current[key]
            }
        })

        const bounds = calculateBounds(dataSources)
        if (!bounds.isEmpty()) {
            map.fitBounds(bounds)
        }

        const mapClickListener = map.addListener('click', () => {
            setInfoWindow((prev) => ({ ...prev, open: false }))
        })

        const mapMouseOutListner = map.addListener('mouseout', () =>
            setInfoWindow({} as IInfoWindow),
        )

        // Clean up the listener when the component unmounts or the map changes
        return () => {
            mapClickListener?.remove()
            mapMouseOutListner?.remove()
        }
    }, [
        map,
        dataSources,
        userLocalization,
        dynamicDataLayersKeys,
        alarmCritical,
        preConfiguredIcons,
        calculateBounds,
    ])

    const onUnmount = useCallback(() => {
        // Remove all data layers when component unmounts
        Object.values(layerRefs.current).forEach((layer) => {
            if (layer) {
                google.maps.event.clearInstanceListeners(layer)
                layer.setMap(null)
            }
        })
        layerRefs.current = {}

        // Remove all circles when component unmounts
        Object.values(circleRefs.current).forEach((circles) => {
            circles.forEach((circle) => circle.setMap(null))
        })
        circleRefs.current = {}

        // Clear the map instance
        if (map) {
            map.unbindAll()
            google.maps.event.clearInstanceListeners(map)
            setMap(null)
        }
    }, [map])

    const onToggleLegend = (source: GeoJSONDataSource) => {
        const updatedDataSources = dataSources.map((ds) => {
            if (getDataSourceKey(ds) === getDataSourceKey(source)) {
                return {
                    ...ds,
                    isSelected: !ds.isSelected,
                }
            }
            return ds
        })
        setDataSources(updatedDataSources)
    }

    useEffect(() => {
        const setUpdatedDataSources = async () => {
            const destinationGeometry =
                initialDataSources.find(
                    (i) =>
                        i.attributeId ===
                        ShipmentAttributes.DestinationLocationAttributeId,
                )?.geojson?.features[0]?.geometry || null

            const destinationCoordinates = toLatLng(destinationGeometry)
            const previousDataSources = dataSourcesRef.current
            const updatedDataSources = await Promise.all(
                initialDataSources.map(async (ds) => {
                    let updatedGeoJson = ds.geojson
                    let routeDetails: {
                        distance?: string
                        duration?: string
                    } | null = null

                    if (
                        destinationCoordinates &&
                        (shipmentStatus?.toLocaleLowerCase() ===
                            ShipmentStatus.IN_TRANSIT ||
                            shipmentStatus?.toLocaleLowerCase() ===
                                ShipmentStatus.AT_ORIGIN) &&
                        (ds.attributeId ===
                            ShipmentAttributes.CurrentLocationAttributeId ||
                            ds.attributeId ===
                                ShipmentAttributes.LocationAttributeId ||
                            ds.attributeId ===
                                ShipmentAttributes.OriginLocationAttributeId)
                    ) {
                        const originGeometry =
                            ds?.geojson?.features[0]?.geometry || null

                        const originCoordinates = toLatLng(originGeometry)

                        if (originCoordinates) {
                            try {
                                routeDetails = await getRoadDistanceAndTime(
                                    originCoordinates,
                                    destinationCoordinates,
                                )
                            } catch {
                                // Ignore routes that cannot be computed for the provided coordinates.
                            }
                        }

                        updatedGeoJson = {
                            ...ds.geojson,
                            features: updatedGeoJson.features.map(
                                (feature, index) => {
                                    return {
                                        ...feature,
                                        properties:
                                            index === 0
                                                ? {
                                                      ...feature.properties,
                                                      ETA: routeDetails?.duration,
                                                      Distance:
                                                          routeDetails?.distance,
                                                  }
                                                : {
                                                      ...feature.properties,
                                                  },
                                    }
                                },
                            ),
                        }
                    }
                    const existingDataSource = previousDataSources.find(
                        (existingDs) =>
                            getDataSourceKey(existingDs) ===
                            getDataSourceKey(ds),
                    )
                    if (existingDataSource) {
                        return {
                            ...ds,
                            geojson: updatedGeoJson,
                            isSelected: existingDataSource.isSelected,
                        } as GeoJSONDataSource
                    } else {
                        return {
                            ...ds,
                            geojson: updatedGeoJson,
                        } as GeoJSONDataSource
                    }
                }),
            )

            setDataSources(updatedDataSources)
            Object.entries(layerRefs.current).forEach(
                ([layerKey, dataLayer]) => {
                    const newSource = updatedDataSources.find(
                        (source) => getDataSourceKey(source) === layerKey,
                    )
                    if (dataLayer && newSource) {
                        dataLayer.forEach((feature) =>
                            dataLayer.remove(feature),
                        )
                        dataLayer.addGeoJson(newSource.geojson)
                    }
                },
            )
            setInfoWindow({
                open: false,
                content: '',
                position: { lat: 0, lng: 0 },
            })
        }
        setUpdatedDataSources()
    }, [initialDataSources, shipmentStatus])

    if (!isLoaded) {
        return <LinearProgress />
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '600px',
            }}
        >
            {/* Map container */}
            <div
                ref={detailMapContainerRef}
                style={{
                    borderRadius: '20px',
                    flexGrow: 1,
                    position: 'relative',
                    background: isDetailFullscreen ? '#fff' : undefined,
                }}
            >
                <GoogleMap
                    id="dynamic-google-map"
                    mapContainerStyle={containerStyle}
                    zoom={options.zoom}
                    onLoad={onLoad}
                    onUnmount={onUnmount}
                    onZoomChanged={handleZoomChanged}
                    options={{
                        ...googleMapOptions,
                        zoomControl: false,
                        fullscreenControl: false,
                    }}
                >
                    {infoWindow?.open && (
                        <InfoBox
                            key={`${infoWindow.position.lat}-${infoWindow.position.lng}`}
                            position={
                                infoWindow?.position
                                    ? new google.maps.LatLng(
                                          infoWindow.position.lat,
                                          infoWindow.position.lng,
                                      )
                                    : undefined
                            }
                            options={{
                                closeBoxURL: '',
                                enableEventPropagation: true,
                                pixelOffset: new window.google.maps.Size(
                                    10,
                                    -80,
                                ),
                                boxStyle: {
                                    minWidth: '210px',
                                },
                            }}
                        >
                            <Box
                                className="marker-tooltip-sub"
                                sx={{
                                    background: '#fff',
                                    padding: '10px 10px 0px 10px',
                                    boxShadow:
                                        '0px 3px 6px 0px rgba(0,0,0,0.4)',
                                    maxHeight: '200px',
                                    overflowY: 'hidden',
                                    '&: hover': {
                                        overflowY: 'auto',
                                    },
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: infoWindow?.content,
                                }}
                                onClick={(event) => event.stopPropagation()}
                                onWheel={(event) => event.stopPropagation()}
                            />
                        </InfoBox>
                    )}
                </GoogleMap>
                {isLoaded && (
                    <MapControls
                        onZoomIn={handleZoomIn}
                        onZoomOut={handleZoomOut}
                        isZoomInDisabled={mapZoom >= maxZoom}
                        isZoomOutDisabled={mapZoom <= minZoom}
                        isFullscreen={isDetailFullscreen}
                        onToggleFullscreen={handleDetailToggleFullscreen}
                    />
                )}
            </div>

            {/* Legend beneath the map */}
            <Box
                sx={{
                    ...legendContainerStyle,
                    flexWrap: 'wrap',
                }}
            >
                {dataSources.map((source, index) => {
                    const config =
                        source?.geojson?.features.at(0)?.properties?.config
                    const preConfiguredColor = config?.color
                    const legendSVG = config?.legendSVG

                    let LegendComponent = null
                    const color = !source.isSelected
                        ? '#808080'
                        : preConfiguredColor

                    /**
                     * attributeName may embed the device trip number as
                     * `${attribute.name} (${tripNumber})` (multi-device case).
                     * Translate the base attribute name and re-append the trip
                     * number as an interpolated placeholder so the label is not
                     * left untranslated (the full concatenated string never
                     * matches a translation key).
                     */
                    const tripNumber = source.entity
                    const tripSuffix = tripNumber ? ` (${tripNumber})` : ''
                    const legendLabel =
                        tripSuffix && source.attributeName.endsWith(tripSuffix)
                            ? t('attributeWithTripNumber', {
                                  attribute: t(
                                      source.attributeName.slice(
                                          0,
                                          -tripSuffix.length,
                                      ),
                                  ),
                                  tripNumber,
                              })
                            : t(source.attributeName)

                    switch (legendSVG) {
                        case 'DashedLine':
                            LegendComponent = <DashedLine color={color} />
                            break
                        case 'LocationMarker':
                            LegendComponent = <LocationMarker color={color} />
                            break
                        case 'VesselIcon':
                            LegendComponent = <VesselIcon color={color} />
                            break
                        case 'SeaPortIcon':
                            LegendComponent = <SeaPortIcon color={color} />
                            break
                        default:
                            LegendComponent = (
                                <div
                                    style={{
                                        backgroundColor: !source.isSelected
                                            ? '#808080'
                                            : preConfiguredIcons &&
                                                preConfiguredColor
                                              ? preConfiguredColor
                                              : source.color,
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                    }}
                                />
                            )
                    }

                    return (
                        <Box
                            id={`legend-item-${source.attributeId}`}
                            key={index}
                            style={{
                                textAlign: 'center',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                            onClick={() => onToggleLegend(source)}
                        >
                            {LegendComponent}
                            <div
                                style={{
                                    marginLeft: '4px',
                                    textWrap: 'nowrap',
                                }}
                            >
                                {legendLabel}
                            </div>
                        </Box>
                    )
                })}
            </Box>
        </div>
    )
}

export default MultiDataSourceMapComponent
